import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Loader2,
  Clock3,
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  AlertTriangle,
  XCircle,
  Lock,
} from "lucide-react";
import { API_URL } from "../config";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

// Load the matching worker from CDN using whatever pdfjs-dist version
// was installed, so it never gets out of sync with the API version.
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const steps = [
  "Upload Document",
  "Automatic Verification",
  "Tamper Detection",
  "Policy & Key Validation",
  "Verification Result",
];

const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };

// Spec section 13 — result classifications. The spec never spells out the
// literal enum string for "TAMPER DETECTED" (it only shows "clean" and
// "suspicious" in JSON examples). "tamper" is assumed here to match the
// existing naming convention — CONFIRM the exact string with the backend
// team; if it differs (e.g. "tamper_detected"), update this one place.
const CLASSIFICATION = {
  CLEAN: "clean",
  SUSPICIOUS: "suspicious",
  TAMPER: "tamper",
};

// Spec section 20 — per-check status values on the final result object.
// These are distinct from the job-progress vocabulary in section 15; since
// we get the finished result directly we map "passed"/"failed"/"skipped"
// straight onto the progress UI's completed/failed/skipped states.
const CHECK_STATUS_MAP = {
  passed: "completed",
  failed: "failed",
  skipped: "skipped",
  warning: "warning",
};

const mapCheckStatus = (status) => CHECK_STATUS_MAP[status] || "pending";

// Section 20 check ids, in display order.
const CHECK_DEFS = [
  { id: "document_structure", title: "Document structure analysis" },
  { id: "content_consistency", title: "Content consistency check" },
  { id: "visual_tamper", title: "Visual tamper detection" },
  { id: "metadata", title: "Metadata analysis" },
  { id: "font_layout", title: "Font & layout verification" },
  { id: "cryptographic_integrity", title: "Cryptographic integrity check" },
];

const initialChecks = () => CHECK_DEFS.map((def) => ({ ...def, status: "pending" }));

// Spec section 31 — full failure-code list, plus one extra
// (ML_MODEL_INSUFFICIENT_DATA) not in the spec but supported by this
// backend.
const FAILURE_CODE_LABELS = {
  PDF_CORRUPTED: "The PDF file appears to be corrupted and could not be parsed.",
  PDF_PASSWORD_PROTECTED: "The PDF is password-protected — remove the password and re-upload.",
  UNSUPPORTED_PDF: "This PDF uses a structure that isn't supported for analysis.",
  OCR_FAILED: "OCR failed on one or more pages.",
  VISION_ANALYSIS_FAILED: "Page image rendering/analysis failed on one or more pages.",
  METADATA_PARSE_FAILED: "Document metadata could not be parsed.",
  CRYPTO_VERIFICATION_FAILED: "The recorded hash algorithm isn't supported for verification.",
  AI_MODEL_UNAVAILABLE: "OCR engine (Tesseract) is not available on the server — visual text cross-check was skipped.",
  WORKER_TIMEOUT: "Visual analysis exceeded its time budget and was stopped early.",
  DOCUMENT_TOO_LARGE: "Document exceeds the size limit for full visual analysis.",
  RESOURCE_LIMIT_EXCEEDED: "Document has too many pages for full visual analysis.",
  ML_MODEL_INSUFFICIENT_DATA: "Not enough verified-clean documents yet to run the anomaly model.",
};

// Failure codes that should not be surfaced to the user in the
// "Partial analysis" banner.
const HIDDEN_FAILURE_CODES = ["ML_MODEL_INSUFFICIENT_DATA"];

// Section 17 clean-example wording — label + "everything's fine" text per
// check, used by the per-dimension integrity summary. The "pass" text is only
// shown when that check's status is "completed"; any other status overrides it.
const INTEGRITY_LABELS = {
  document_structure: { label: "Document integrity", pass: "Verified" },
  content_consistency: { label: "Content integrity", pass: "Consistent" },
  visual_tamper: { label: "Visual integrity", pass: "Consistent" },
  metadata: { label: "Metadata integrity", pass: "No significant anomaly" },
  font_layout: { label: "Font/layout integrity", pass: "Consistent" },
  cryptographic_integrity: { label: "Cryptographic integrity", pass: "Verified" },
};

const INTEGRITY_STATUS_STYLE = {
  failed: { text: "Failed", colorClass: "text-red-600" },
  skipped: { text: "Skipped — not evaluated", colorClass: "text-gray-400" },
  warning: { text: "Warning", colorClass: "text-yellow-600" },
  pending: { text: "Pending", colorClass: "text-gray-400" },
};

// Confirmed against the real backend route: @pdf_bp.route("/ai-tamper-detection",
// methods=["POST"]) — NOT the spec's documented /api/v1/ipqsign/tamper-analysis,
// which this backend doesn't actually implement. Status polling below shares
// this same base path (/ai-tamper-detection/status/<id>).
const TAMPER_ANALYSIS_ENDPOINT = `${API_URL}/ai-tamper-detection`;

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 120000; // 2 minutes — matches spec §30's outer bound
const MAX_CONSECUTIVE_ERRORS = 4;

const IMAGE_FILE_REGEX = /\.(png|jpg|jpeg|gif|bmp|webp)$/i;

/* ---- Step progress styling ---- */

const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 border-green-500 text-white",
  current: "bg-blue-600 border-blue-600 text-white",
  upcoming: "bg-white border-gray-300 text-gray-500",
};

const STEP_LABEL_CLASS = {
  done: "text-green-600",
  current: "text-blue-600",
  upcoming: "text-gray-500",
};

/* ---- Per-check progress rows ---- */

const CHECK_ICON = {
  completed: { Icon: CheckCircle2, className: "text-green-500" },
  running: { Icon: Loader2, className: "text-blue-500 animate-spin" },
  pending: { Icon: Clock3, className: "text-gray-300" },
  failed: { Icon: XCircle, className: "text-red-500" },
  warning: { Icon: AlertTriangle, className: "text-yellow-500" },
  skipped: { Icon: Clock3, className: "text-gray-400" },
};

const CHECK_LABEL = {
  completed: { text: "Passed", className: "text-green-600" },
  running: { text: "In Progress", className: "text-blue-600" },
  pending: { text: "Pending", className: "text-gray-400" },
  failed: { text: "Failed", className: "text-red-600" },
  warning: { text: "Warning", className: "text-yellow-600" },
  skipped: { text: "Skipped", className: "text-gray-400" },
};

/* ---- Severity styling (critical & high share a look in most places) ---- */

const SEVERITY_GROUP = { critical: "critical", high: "critical", medium: "medium", low: "low" };

const PAGE_BUTTON_STYLES = {
  critical: {
    active: "bg-red-600 text-white border-red-600",
    idle: "bg-red-50 text-red-700 border-red-300 hover:bg-red-100",
  },
  medium: {
    active: "bg-yellow-500 text-white border-yellow-500",
    idle: "bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100",
  },
  low: {
    active: "bg-gray-500 text-white border-gray-500",
    idle: "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200",
  },
  none: {
    active: "bg-blue-600 text-white border-blue-600",
    idle: "bg-white text-gray-600 border-gray-200 hover:bg-gray-100",
  },
};

const CANVAS_BORDER_STYLES = {
  critical: "border-4 border-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.15)]",
  medium: "border-4 border-yellow-500 shadow-[0_0_0_4px_rgba(234,179,8,0.15)]",
  low: "border-4 border-gray-400",
  none: "border border-gray-200",
};

const PAGE_FINDING_DOT = {
  critical: "bg-red-500",
  medium: "bg-yellow-500",
  low: "bg-gray-400",
  none: "bg-gray-400",
};

const FINDING_DOT = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-500",
};

const BOX_COLORS = {
  high: { stroke: "rgba(239,68,68,0.9)", fill: "rgba(239,68,68,0.15)" },
  other: { stroke: "rgba(234,179,8,0.9)", fill: "rgba(234,179,8,0.15)" },
};

/* ---- Result panel styling ---- */

const TONE_STYLES = {
  pending: { panel: "bg-gray-50", text: "text-blue-600", bar: "bg-blue-500" },
  clean: { panel: "bg-green-50 border-green-200", text: "text-green-600", bar: "bg-green-500" },
  suspicious: { panel: "bg-yellow-50 border-yellow-200", text: "text-yellow-600", bar: "bg-yellow-500" },
  tamper: { panel: "bg-red-50 border-red-200", text: "text-red-600", bar: "bg-red-500" },
};

const TONE_HEADLINE = {
  pending: "Analysis in progress...",
  clean: "No tampering detected",
  suspicious: "Potential tampering detected",
  tamper: "Tampering detected",
};

const TONE_ICON = {
  clean: CheckCircle2,
  suspicious: AlertTriangle,
  tamper: XCircle,
};

const LOCK_MESSAGES = {
  locked: "This document is password-protected. Enter the password to continue.",
  checking: "Checking document...",
};
const ENCRYPTED_PREVIEW_MESSAGE =
  "Encrypted document — the preview appears once the analysis completes.";

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                */
/* -------------------------------------------------------------------------- */

function getStepStatus(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

// Classification -> one of pending | clean | suspicious | tamper.
// "suspicious" is the one value confirmed to match the real backend output;
// "tamper" is anything else once analysis is done and it isn't clean or
// suspicious, so a mismatch on the backend's exact tamper string can't cause
// the icon/label to silently disappear.
function getResultTone(done, classification) {
  if (!done) return "pending";
  if (classification === CLASSIFICATION.CLEAN) return "clean";
  return classification === CLASSIFICATION.SUSPICIOUS ? "suspicious" : "tamper";
}

function groupFindingsByPage(findings) {
  const grouped = {};
  (findings || []).forEach((f) => {
    if (!f.page) return;
    if (!grouped[f.page]) grouped[f.page] = [];
    grouped[f.page].push(f);
  });
  return grouped;
}

function maxSeverityForPage(findingsByPage, pageNum) {
  const list = findingsByPage[pageNum];
  if (!list || list.length === 0) return null;
  return list.reduce(
    (max, f) => (SEVERITY_RANK[f.severity] > SEVERITY_RANK[max] ? f.severity : max),
    list[0].severity
  );
}

const severityGroup = (severity) => SEVERITY_GROUP[severity] || "none";

function pageButtonClass(severity, isActive) {
  const styles = PAGE_BUTTON_STYLES[severityGroup(severity)];
  return isActive ? styles.active : styles.idle;
}

function pluralize(count, word) {
  return `${count} ${word}${count > 1 ? "s" : ""}`;
}

function findingsSummary(count, isTamperLike) {
  if (isTamperLike) return `${pluralize(count, "critical finding")} identified`;
  return `${count} ${count > 1 ? "anomalies" : "anomaly"} identified`;
}

function integrityDisplay(status, meta) {
  if (status === "completed") return { text: meta.pass, colorClass: "text-green-600" };
  return INTEGRITY_STATUS_STYLE[status] || INTEGRITY_STATUS_STYLE.pending;
}

// Spec section 12/20 — confidence_score / risk_score are normalized 0.00–1.00
// and must be scaled to 0–100 for display. Display-only: when the result is
// clean, every check passed and there are no findings, show 100%. Otherwise
// show the backend's own value.
function computeScores(result) {
  const allChecksPassed = Object.values(result.checks || {}).every(
    (c) => c?.status === "passed"
  );
  const noFindings = (result.findings || []).length === 0;
  const isCleanResult = result.classification === CLASSIFICATION.CLEAN;
  const rawConfidence = Math.round((result.confidence_score ?? 0) * 100);

  return {
    confidence: isCleanResult && allChecksPassed && noFindings ? 100 : rawConfidence,
    risk: Math.round((result.risk_score ?? 0) * 100),
  };
}

// Section 20 — checks are nested under result.checks.<key>.status.
function buildChecksFromResult(result) {
  const resultChecks = result.checks || {};
  return CHECK_DEFS.map((def) => ({
    ...def,
    status: mapCheckStatus(resultChecks[def.id]?.status),
  }));
}

function failureMessage(payload) {
  const code = payload.failure_codes?.[0] || payload.error_code;
  return (
    payload.error ||
    FAILURE_CODE_LABELS[code] ||
    "AI Tamper Detection failed while processing."
  );
}

function getVisibleFailureCodes(analysisResult) {
  return (analysisResult.failure_codes || []).filter(
    (code) => !HIDDEN_FAILURE_CODES.includes(code)
  );
}

function confidenceSuffix(confidence) {
  return typeof confidence === "number" ? ` · ${Math.round(confidence * 100)}% confidence` : "";
}

/* ---- PDF helpers ---- */

function classifyProbeError(err) {
  // Not a password problem — let the backend report it.
  if (err?.name !== "PasswordException") return "ok";
  return err.code === 2 ? "wrong" : "need";
}

// Returns "ok" | "need" | "wrong". Never throws.
async function probePdf(url, password) {
  try {
    const doc = await pdfjsLib.getDocument({ url, password }).promise;
    doc.destroy();
    return "ok";
  } catch (err) {
    return classifyProbeError(err);
  }
}

async function findMatchingPages(pdfDoc, numPages, query) {
  const matches = [];
  try {
    for (let i = 1; i <= numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map((it) => it.str).join(" ").toLowerCase();
      if (pageText.includes(query)) matches.push(i);
    }
  } catch (err) {
    console.error("Search failed:", err);
  }
  return matches;
}

// bbox values come from the backend's fitz/PyMuPDF render, which — unlike raw
// PDF space — uses a top-left origin, same as canvas pixels. So this is a
// direct scale by zoom (points → pixels), NOT a PDF-space→viewport conversion
// (viewport.convertToViewportPoint assumes bottom-left-origin input and would
// mirror the Y axis). CONFIRM this origin assumption with the backend team —
// if the renderer changes, this overlay will silently mis-place every box.
function drawFindingBox(context, finding, zoom) {
  const bbox = finding.evidence?.bbox;
  if (!bbox || bbox.length !== 4) return;

  const [x0, y0, x1, y1] = bbox;
  const rx = Math.min(x0, x1) * zoom;
  const ry = Math.min(y0, y1) * zoom;
  const rw = Math.abs(x1 - x0) * zoom;
  const rh = Math.abs(y1 - y0) * zoom;

  const colors = severityGroup(finding.severity) === "critical" ? BOX_COLORS.high : BOX_COLORS.other;
  context.strokeStyle = colors.stroke;
  context.fillStyle = colors.fill;
  context.lineWidth = 2;
  // pad the box slightly so it doesn't hug the text too tightly
  context.fillRect(rx - 4, ry - 4, rw + 8, rh + 8);
  context.strokeRect(rx - 4, ry - 4, rw + 8, rh + 8);
}

/* ---- Analysis API helpers ---- */

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// The real backend only accepts multipart (request.files.get("pdf")), not the
// JSON/document_id body the spec's own example shows — so always send the file.
function buildAnalysisFormData({ file, verificationResult, analysisProfile, password }) {
  const signatureId =
    verificationResult?.signature_id ||
    verificationResult?.signatureId ||
    file?.signature_id;

  const formData = new FormData();
  formData.append("pdf", file.file);
  formData.append("analysis_profile", analysisProfile || "standard");
  if (signatureId) formData.append("signature_id", signatureId);

  // The file may be a password-protected PDF (signed with Document
  // Encryption). Without the password the backend cannot open the PDF and the
  // job never finishes.
  if (password) formData.append("encryption_password", password);
  return formData;
}

async function readErrorBodyMessage(response) {
  const fallback = `Status check failed (HTTP ${response.status}).`;
  try {
    const errBody = await response.json();
    return errBody?.error || fallback;
  } catch {
    return fallback; // not JSON
  }
}

// A 401/404/500 from the status route has no `status` field, which used to be
// treated as "still processing" and polled until timeout — so surface it.
async function fetchAnalysisStatus(id) {
  const response = await fetch(`${TAMPER_ANALYSIS_ENDPOINT}/status/${id}`, {
    headers: authHeaders(),
  });
  if (response.ok) return { payload: await response.json() };
  return { error: { status: response.status, message: await readErrorBodyMessage(response) } };
}

// Job accepted (202) — real async path — vs. a synchronous full result.
const isAcceptedJob = (result) =>
  Boolean(result.analysis_id && result.status && !("classification" in result));

/* -------------------------------------------------------------------------- */
/* Hooks                                                                       */
/* -------------------------------------------------------------------------- */

// Decides what the poll loop should do with a status payload:
// "completed" | "failed" | "timeout" | "continue".
function pollDecision(payload, elapsedMs) {
  if (payload.status === "completed") return "completed";
  if (payload.status === "failed") return "failed";
  // "queued" / "processing" — keep polling unless we've timed out.
  return elapsedMs > POLL_TIMEOUT_MS ? "timeout" : "continue";
}

const POLL_TIMEOUT_MESSAGE =
  "AI Tamper Detection is taking longer than expected. " +
  "The analysis worker may be unable to process this file.";

const WRONG_PASSWORD_MESSAGE = "Incorrect password. Please try again.";

// Returns an error message, or "" when the typed password opens the PDF.
async function validateUnlockPassword(url, password) {
  if (!password) return "Please enter the PDF password.";
  const result = await probePdf(url, password);
  return result === "ok" ? "" : WRONG_PASSWORD_MESSAGE;
}

// Runs the initial "is this PDF openable?" probe once on mount.
function useInitialProbe({ file, shouldProbe, onResult }) {
  useEffect(() => {
    if (!shouldProbe) return;
    let cancelled = false;
    probePdf(file.url, file.encryptionPassword).then((result) => {
      if (!cancelled) onResult(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);
}

// Encrypted-PDF gate: analysis and preview only start once the PDF is
// confirmed openable — either it isn't password-protected, or a correct
// password is available (carried from the verify step, or typed in the prompt).
function useUnlockGate({ file, hasFile, isPdfFile }) {
  const [pagePassword, setPagePassword] = useState("");
  const [unlock, setUnlock] = useState(isPdfFile ? "checking" : "ready"); // checking | locked | ready
  const [unlockInput, setUnlockInput] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const effectivePassword = file?.encryptionPassword || pagePassword || undefined;

  const submitUnlock = async () => {
    const pw = unlockInput.trim();
    const error = await validateUnlockPassword(file.url, pw);
    if (error) {
      setUnlockError(error);
      return;
    }
    setPagePassword(pw);
    setUnlockError("");
    setUnlock("ready");
  };

  const applyProbeResult = (result) => {
    if (result === "wrong") setUnlockError(WRONG_PASSWORD_MESSAGE);
    setUnlock(result === "ok" ? "ready" : "locked");
  };

  useInitialProbe({ file, shouldProbe: hasFile && isPdfFile, onResult: applyProbeResult });

  const onUnlockInputChange = (value) => {
    setUnlockInput(value);
    setUnlockError("");
  };

  return {
    unlock,
    unlockInput,
    unlockError,
    effectivePassword,
    submitUnlock,
    onUnlockInputChange,
  };
}

function useTamperAnalysis({
  file,
  hasFile,
  unlock,
  effectivePassword,
  verificationResult,
  analysisProfile,
  navigate,
}) {
  const [analysisResult, setAnalysisResult] = useState(null);
  const [checks, setChecks] = useState(initialChecks);
  // Risk/confidence are stored already scaled to a 0–100 percentage.
  const [confidenceScore, setConfidenceScore] = useState(0);
  const [riskScore, setRiskScore] = useState(0);
  const [done, setDone] = useState(false);
  const pollingTimeoutRef = useRef(null);

  // Applies a completed analysis payload (same shape whether it came from the
  // status-poll endpoint or directly from the POST response).
  const applyAnalysisResult = (result) => {
    const { confidence, risk } = computeScores(result);
    setAnalysisResult(result);
    setConfidenceScore(confidence);
    setRiskScore(risk);
    setDone(true);
    setChecks(buildChecksFromResult(result));
  };

  // Single place to stop the spinner when the job can't finish, so the page
  // never sits on "Analysis in progress..." forever.
  const failAnalysis = (message) => {
    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    setChecks((prev) => prev.map((c) => ({ ...c, status: "failed" })));
    alert(message);
  };

  // Section 19.2 — poll /ai-tamper-detection/status/<id> until the Celery task
  // is "completed" or "failed", then hand the payload to applyAnalysisResult.
  const pollAnalysisStatus = (id) => {
    const startedAt = Date.now();
    let consecutiveErrors = 0;

    const scheduleNext = () => {
      pollingTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const decisionActions = {
      completed: (payload) => applyAnalysisResult(payload),
      failed: (payload) => failAnalysis(failureMessage(payload)),
      timeout: () => failAnalysis(POLL_TIMEOUT_MESSAGE),
      continue: scheduleNext,
    };

    const handlePayload = (payload) => {
      decisionActions[pollDecision(payload, Date.now() - startedAt)](payload);
    };

    // Transient network hiccup — retry a few times, then stop.
    const handlePollError = (err) => {
      consecutiveErrors += 1;
      console.error("Polling failed:", err);
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        failAnalysis(err.message || "Lost connection while checking analysis status.");
        return;
      }
      scheduleNext();
    };

    const tick = async () => {
      try {
        const { payload, error } = await fetchAnalysisStatus(id);

        if (error?.status === 401) {
          failAnalysis(error.message);
          navigate("/login");
          return;
        }
        if (error) throw new Error(error.message);

        consecutiveErrors = 0;
        handlePayload(payload);
      } catch (err) {
        handlePollError(err);
      }
    };

    tick();
  };

  // Auth/server errors come back as { error: "..." } with no
  // classification/confidence/checks — don't render those as a real result.
  const handleStartFailure = (status, result) => {
    if (status === 401) {
      alert(result.error || "Authentication failed. Please log in again.");
      navigate("/login");
      return;
    }
    alert(result.error || "AI Tamper Detection failed to start.");
  };

  // Spec section 19.1 — submit the analysis job.
  const runAITamperDetection = async () => {
    try {
      const response = await fetch(TAMPER_ANALYSIS_ENDPOINT, {
        method: "POST",
        headers: authHeaders(),
        body: buildAnalysisFormData({
          file,
          verificationResult,
          analysisProfile,
          password: effectivePassword,
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        handleStartFailure(response.status, result);
        return;
      }

      if (isAcceptedJob(result)) {
        // Show all checks as "running" while we wait, then poll for the outcome.
        setChecks((prev) => prev.map((c) => ({ ...c, status: "running" })));
        pollAnalysisStatus(result.analysis_id);
        return;
      }

      // Defensive fallback: a synchronous response still works unchanged.
      applyAnalysisResult(result);
    } catch (err) {
      console.error(err);
      alert("AI Tamper Detection Failed");
    }
  };

  // Start the analysis only after the document is unlocked.
  useEffect(() => {
    if (!hasFile || unlock !== "ready") return;
    runAITamperDetection();
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [unlock]);

  return { analysisResult, checks, confidenceScore, riskScore, done };
}

// Loads the PDF for canvas-based viewing once analysis is done, so pages with
// findings can be visually highlighted (a plain <iframe> can't be annotated).
function usePdfPreview({ done, file, isPdfFile, effectivePassword, findingsByPage }) {
  const canvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfPreviewError, setPdfPreviewError] = useState(false);
  const [zoomScale, setZoomScale] = useState(1.3);

  useEffect(() => {
    if (!done || !file?.url || !isPdfFile) return;

    let cancelled = false;

    pdfjsLib.getDocument({ url: file.url, password: effectivePassword }).promise
      .then((doc) => {
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setCurrentPage(1);
      })
      .catch((err) => {
        console.error("Failed to load PDF for page-level preview:", err);
        setPdfPreviewError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [done, file?.url, isPdfFile]);

  // Render the selected page onto the canvas, then overlay bounding boxes for
  // any findings with object-level evidence on this page (spec section 16).
  useEffect(() => {
    if (!pdfDocRef.current || !canvasRef.current || currentPage < 1) return;

    let cancelled = false;

    pdfDocRef.current.getPage(currentPage).then((page) => {
      if (cancelled || !canvasRef.current) return;
      const viewport = page.getViewport({ scale: zoomScale });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      page.render({ canvasContext: context, viewport }).promise.then(() => {
        if (cancelled) return;
        (findingsByPage[currentPage] || []).forEach((f) => drawFindingBox(context, f, zoomScale));
      });
    });

    return () => {
      cancelled = true;
    };
  }, [currentPage, numPages, zoomScale, findingsByPage]);

  return {
    canvasRef,
    pdfDocRef,
    numPages,
    currentPage,
    setCurrentPage,
    pdfPreviewError,
    zoomScale,
    setZoomScale,
  };
}

function useDocumentSearch(pdfDocRef, numPages, onFirstMatch) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMatches, setSearchMatches] = useState([]); // page numbers containing the query
  const [searching, setSearching] = useState(false);

  const runSearch = async () => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !pdfDocRef.current) {
      setSearchMatches([]);
      return;
    }

    setSearching(true);
    const matches = await findMatchingPages(pdfDocRef.current, numPages, q);
    setSearchMatches(matches);
    setSearching(false);
    if (matches.length > 0) onFirstMatch(matches[0]);
  };

  return { searchQuery, setSearchQuery, searchMatches, searching, runSearch };
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function StepProgress({ currentStep }) {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => {
        const status = getStepStatus(index, currentStep);
        const isLast = index === steps.length - 1;

        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {!isLast && (
              <div
                className={`absolute top-5 left-1/2 w-full h-[2px] ${
                  status === "done" ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
            <div
              className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_CIRCLE_CLASS[status]}`}
            >
              {status === "done" ? <Check size={12} /> : index + 1}
            </div>
            <p
              className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_LABEL_CLASS[status]}`}
            >
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ChecksPanel({ checks }) {
  return (
    <div className="bg-gray-50 border rounded-2xl p-3 sm:p-5 md:p-6">
      <h3 className="text-20px font-semibold text-gray-700 mb-2">AI Analysis Progress</h3>
      <div className="space-y-4">
        {checks.map((item) => {
          const icon = CHECK_ICON[item.status];
          const label = CHECK_LABEL[item.status];
          return (
            <div
              key={item.id}
              className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white"
            >
              <div className="flex items-center gap-4">
                {icon && <icon.Icon size={20} className={icon.className} />}
                <p className="text-gray-800 text-16px">{item.title}</p>
              </div>
              <div>
                {label && (
                  <span className={`${label.className} font-semibold text-14px`}>{label.text}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SearchZoomBar({ search, zoomScale, onZoomChange }) {
  const { searchQuery, setSearchQuery, searchMatches, searching, runSearch } = search;

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b bg-white">
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") runSearch();
        }}
        placeholder="Search document text..."
        className="flex-1 h-8 px-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-blue-500"
      />
      <button
        onClick={runSearch}
        disabled={searching}
        className="h-8 px-3 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
      >
        {searching ? "..." : "Find"}
      </button>
      {searchMatches.length > 0 && (
        <span className="text-xs text-gray-500 flex-shrink-0">
          {pluralize(searchMatches.length, "page")}
        </span>
      )}
      <div className="flex items-center gap-1 flex-shrink-0 border-l pl-2 ml-1">
        <button
          onClick={() => onZoomChange((z) => Math.max(0.5, +(z - 0.2).toFixed(1)))}
          className="w-7 h-7 text-sm font-bold bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          −
        </button>
        <span className="text-xs text-gray-500 w-10 text-center">{Math.round(zoomScale * 100)}%</span>
        <button
          onClick={() => onZoomChange((z) => Math.min(3, +(z + 0.2).toFixed(1)))}
          className="w-7 h-7 text-sm font-bold bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          +
        </button>
      </div>
    </div>
  );
}

// Colored by the worst finding severity on each page.
function PageSelector({ numPages, currentPage, findingsByPage, searchMatches, onSelect }) {
  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div className="flex items-center gap-1.5 px-3 py-2 border-b bg-gray-50 overflow-x-auto">
      {pages.map((pageNum) => {
        const sev = maxSeverityForPage(findingsByPage, pageNum);
        return (
          <button
            key={pageNum}
            onClick={() => onSelect(pageNum)}
            className={`relative flex-shrink-0 w-8 h-8 rounded-lg border text-xs font-semibold transition ${pageButtonClass(sev, pageNum === currentPage)}`}
            title={sev ? `Page ${pageNum} — ${sev} severity finding` : `Page ${pageNum}`}
          >
            {pageNum}
            {searchMatches.includes(pageNum) && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white" />
            )}
          </button>
        );
      })}
    </div>
  );
}

function PageFindings({ page, findings }) {
  return (
    <div className="border-t bg-white px-4 py-3 space-y-1.5 max-h-32 overflow-y-auto">
      <p className="text-xs font-semibold text-gray-500">Findings on page {page}:</p>
      {findings.map((f) => (
        <p key={f.finding_id} className="text-xs text-gray-700">
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${PAGE_FINDING_DOT[severityGroup(f.severity)]}`}
          />
          {f.description}
        </p>
      ))}
    </div>
  );
}

function AnnotatedViewer({ preview, search, findingsByPage }) {
  const { canvasRef, numPages, currentPage, setCurrentPage, zoomScale, setZoomScale } = preview;
  const sev = maxSeverityForPage(findingsByPage, currentPage);
  const pageFindings = findingsByPage[currentPage];

  return (
    <>
      <SearchZoomBar search={search} zoomScale={zoomScale} onZoomChange={setZoomScale} />
      <PageSelector
        numPages={numPages}
        currentPage={currentPage}
        findingsByPage={findingsByPage}
        searchMatches={search.searchMatches}
        onSelect={setCurrentPage}
      />

      {/* CANVAS RENDER — bordered/tinted if the current page has a finding */}
      <div
        className="flex-1 bg-gray-100 flex items-center justify-center overflow-auto p-4"
        style={{ minHeight: "360px" }}
      >
        <canvas
          ref={canvasRef}
          className={`max-w-full rounded ${CANVAS_BORDER_STYLES[severityGroup(sev)]}`}
        />
      </div>

      {pageFindings?.length > 0 && <PageFindings page={currentPage} findings={pageFindings} />}
    </>
  );
}

function PlainPreview({ hasFile, isPdfFile, file, unlock, effectivePassword }) {
  if (!hasFile) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 text-gray-400">
        <FileText size={48} />
        <p className="text-sm">No document uploaded</p>
      </div>
    );
  }

  if (isPdfFile) {
    if (unlock === "ready" && !effectivePassword) {
      return (
        <iframe
          src={`${file.url}#pagemode=none&toolbar=0`}
          title="Document Preview"
          className="w-full h-full"
          style={{ minHeight: "400px" }}
        />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-3 text-gray-400 p-6 text-center">
        <Lock size={48} />
        <p className="text-sm">{LOCK_MESSAGES[unlock] || ENCRYPTED_PREVIEW_MESSAGE}</p>
      </div>
    );
  }

  if (IMAGE_FILE_REGEX.test(file?.name)) {
    return (
      <img
        src={file.url}
        alt={file.name}
        className="max-w-full max-h-full object-contain p-4"
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3 text-gray-400 p-6">
      <FileText size={48} />
      <p className="text-sm">Preview not available for this file type</p>
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 text-sm hover:underline"
      >
        Open in new tab
      </a>
    </div>
  );
}

function DocumentPreviewPanel({
  file,
  hasFile,
  isPdfFile,
  done,
  unlock,
  effectivePassword,
  preview,
  search,
  findingsByPage,
}) {
  const flaggedCount = Object.keys(findingsByPage).length;
  const pdfReady = done && isPdfFile && preview.numPages > 0;
  const showAnnotated = pdfReady && !preview.pdfPreviewError;

  return (
    <div className="border rounded-2xl overflow-hidden flex flex-col min-h-[300px] md:min-h-0">
      <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between gap-2">
        <p className="font-semibold text-gray-700 text-sm truncate">
          {file?.name || "Document Preview"}
        </p>
        {pdfReady && flaggedCount > 0 && (
          <span className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 flex-shrink-0">
            {pluralize(flaggedCount, "page")} flagged
          </span>
        )}
      </div>

      {showAnnotated ? (
        <AnnotatedViewer preview={preview} search={search} findingsByPage={findingsByPage} />
      ) : (
        <div
          className="flex-1 bg-gray-100 flex items-center justify-center overflow-auto"
          style={{ minHeight: "400px" }}
        >
          <PlainPreview
            hasFile={hasFile}
            isPdfFile={isPdfFile}
            file={file}
            unlock={unlock}
            effectivePassword={effectivePassword}
          />
        </div>
      )}
    </div>
  );
}

function PartialAnalysisBanner({ analysisResult }) {
  if (analysisResult?.analysis_status !== "partial") return null;

  const visibleCodes = getVisibleFailureCodes(analysisResult);
  if (visibleCodes.length === 0) return null;

  return (
    <div className="mb-3 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
      <AlertTriangle size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-orange-700">
          Partial analysis — some checks could not fully run
        </p>
        <ul className="text-xs text-orange-600 mt-1 list-disc list-inside">
          {visibleCodes.map((code) => (
            <li key={code}>{FAILURE_CODE_LABELS[code] || code}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Section 17 per-dimension integrity lines — driven by the ACTUAL per-check
// status, never hardcoded to green. Section 31: a failed AI component must not
// silently produce a clean result, so a skipped/failed check shows as such
// even when the overall classification is "clean".
function IntegrityLines({ checks }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
      {checks.map((c) => {
        const meta = INTEGRITY_LABELS[c.id] || { label: c.title, pass: "Verified" };
        const { text, colorClass } = integrityDisplay(c.status, meta);
        return (
          <p key={c.id}>
            {meta.label}: <span className={`${colorClass} font-medium`}>{text}</span>
          </p>
        );
      })}
    </div>
  );
}

// Section 18/25 — structured evidence (before/after).
function FindingEvidence({ evidence }) {
  if (!evidence?.original && !evidence?.observed) return null;

  return (
    <p className="text-xs text-gray-500 mt-0.5">
      {evidence.original && (
        <>Original: <span className="font-mono">{evidence.original}</span></>
      )}
      {evidence.original && evidence.observed && " → "}
      {evidence.observed && (
        <>Observed: <span className="font-mono">{evidence.observed}</span></>
      )}
    </p>
  );
}

function FindingItem({ finding }) {
  return (
    <div className="flex items-start gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
      <span
        className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${FINDING_DOT[finding.severity] || "bg-gray-400"}`}
      />
      <div>
        <p className="text-sm text-gray-800">
          {finding.description}
          {finding.page ? ` (page ${finding.page})` : ""}
        </p>
        <FindingEvidence evidence={finding.evidence} />
        <p className="text-xs text-gray-400 capitalize">
          {finding.severity} severity
          {confidenceSuffix(finding.confidence)}
        </p>
      </div>
    </div>
  );
}

function FindingsList({ findings, isTamperLike }) {
  return (
    <div className="mt-4 space-y-2">
      <p className="text-sm font-semibold text-gray-700">
        {findingsSummary(findings.length, isTamperLike)}
      </p>
      {findings.map((f) => (
        <FindingItem key={f.finding_id} finding={f} />
      ))}
    </div>
  );
}

function ResultPanel({ hasFile, done, tone, analysisResult, checks, headlineLabel, headlineValue }) {
  const styles = TONE_STYLES[tone];
  const ToneIcon = TONE_ICON[tone];
  const headline = hasFile ? TONE_HEADLINE[tone] : "No document to analyze";
  const findings = analysisResult?.findings || [];

  return (
    <div className={`border rounded-2xl p-3 sm:p-5 md:p-6 mb-4 ${styles.panel}`}>
      <h3 className="text-xl font-semibold text-gray-700 mb-3">AI Tamper Detection Result</h3>

      {done && <PartialAnalysisBanner analysisResult={analysisResult} />}

      <div className={`flex items-center gap-2 font-semibold text-lg ${styles.text}`}>
        {ToneIcon && <ToneIcon size={20} />}
        <p>{headline}</p>
      </div>

      {hasFile && (
        <>
          {/* Section 17 — label/metric switches between confidence (clean)
              and risk score (suspicious/tamper). */}
          <p className="text-gray-500 mt-1 text-[14px]">
            {headlineLabel}: {headlineValue}%
          </p>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
            <div
              className={`h-3 rounded-full transition-all duration-700 ${styles.bar}`}
              style={{ width: `${headlineValue}%` }}
            />
          </div>

          {done && <IntegrityLines checks={checks} />}
          {done && findings.length > 0 && (
            <FindingsList findings={findings} isTamperLike={tone === "tamper"} />
          )}
        </>
      )}
    </div>
  );
}

// Shown when the PDF cannot be opened without a password.
function PasswordPrompt({ gate, onCancel }) {
  const { unlockInput, unlockError, onUnlockInputChange, submitUnlock } = gate;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Lock className="text-blue-600" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Document is encrypted</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Enter the password to run tamper detection on this document.
        </p>
        <input
          type="password"
          autoFocus
          value={unlockInput}
          onChange={(e) => onUnlockInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitUnlock();
          }}
          placeholder="Enter PDF password"
          className={`w-full h-[44px] px-3 rounded-[10px] border text-sm outline-none ${
            unlockError
              ? "border-red-400 focus:border-red-500"
              : "border-gray-300 focus:border-blue-500"
          }`}
        />
        {unlockError && <p className="text-red-500 text-xs mt-2">{unlockError}</p>}
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onCancel}
            className="h-[40px] px-5 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={submitUnlock}
            className="h-[40px] px-5 bg-[#2563EB] text-white rounded-[10px] hover:bg-[#1D4ED8] transition text-sm font-medium"
          >
            Unlock
          </button>
        </div>
      </div>
    </div>
  );
}

function FooterButtons({ onCancel, onNext }) {
  return (
    <div className="flex justify-between mt-3">
      <button
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
      >
        <ArrowLeft size={14} /> Cancel
      </button>
      <button
        onClick={onNext}
        className="flex items-center gap-1.5 h-[44px] px-6 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition"
      >
        Next <ArrowRight size={14} />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function AITamperDetection() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentStep = 2;

  const verificationResult = location.state?.verificationResult || {};
  const uploadedFiles = location.state?.uploadedFiles || [];
  const hasFile = uploadedFiles.length > 0;
  const file = uploadedFiles[0];
  const isPdfFile = file?.name?.toLowerCase().endsWith(".pdf");

  const gate = useUnlockGate({ file, hasFile, isPdfFile });
  const analysis = useTamperAnalysis({
    file,
    hasFile,
    unlock: gate.unlock,
    effectivePassword: gate.effectivePassword,
    verificationResult,
    analysisProfile: location.state?.analysisProfile,
    navigate,
  });
  const { analysisResult, checks, confidenceScore, riskScore, done } = analysis;

  const findingsByPage = useMemo(
    () => groupFindingsByPage(analysisResult?.findings),
    [analysisResult]
  );

  const preview = usePdfPreview({
    done,
    file,
    isPdfFile,
    effectivePassword: gate.effectivePassword,
    findingsByPage,
  });
  const search = useDocumentSearch(preview.pdfDocRef, preview.numPages, preview.setCurrentPage);

  const handleNext = () => {
    if (!done) {
      alert("Please wait until AI analysis is complete.");
      return;
    }
    navigate("/policy-key-validation", {
      state: {
        uploadedFiles,
        aiScore: confidenceScore,
        riskScore,
        classification: analysisResult?.classification,
        analysisResult,
        verificationResult,
      },
    });
  };

  const handleCancel = () => {
    navigate("/automatic-verification", { state: { uploadedFiles } });
  };

  // Section 17 — the headline metric switches by classification: CLEAN shows
  // "AI confidence score", anything else shows "Risk score".
  const tone = getResultTone(done, analysisResult?.classification);
  const isNotClean = tone === "suspicious" || tone === "tamper";
  const headlineLabel = isNotClean ? "Risk score" : "AI confidence score";
  const headlineValue = isNotClean ? riskScore : confidenceScore;

  return (
    <div className="w-full bg-white mx-auto">
      <StepProgress currentStep={currentStep} />

      {/* MAIN CONTENT — two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <ChecksPanel checks={checks} />
        <DocumentPreviewPanel
          file={file}
          hasFile={hasFile}
          isPdfFile={isPdfFile}
          done={done}
          unlock={gate.unlock}
          effectivePassword={gate.effectivePassword}
          preview={preview}
          search={search}
          findingsByPage={findingsByPage}
        />
      </div>

      <ResultPanel
        hasFile={hasFile}
        done={done}
        tone={tone}
        analysisResult={analysisResult}
        checks={checks}
        headlineLabel={headlineLabel}
        headlineValue={headlineValue}
      />

      {gate.unlock === "locked" && <PasswordPrompt gate={gate} onCancel={handleCancel} />}

      <FooterButtons onCancel={handleCancel} onNext={handleNext} />
    </div>
  );
}